import { Button, Col, Row } from 'antd';
import classNames from 'classnames';
import { isEmpty, isObject } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';

import routes from '../../../app/routes';
import { FAIcon } from '../../../components/adapters/fontAwesomeAdapters';
import { AsyncCallbackButton } from '../../../components/data/asyncActionElements';
import { FormItemsOverrideContextProvider } from '../../../components/forms/FormItem';
import { FormItemInputText } from '../../../components/forms/basicFormElements';
import { useDynamicOriginalValueDependency } from '../../../components/forms/dynamic/dynamicFormDependencies';
import { NoLabelForm } from '../../../components/forms/forms';
import { ConfigurableCard } from '../../../components/layout/cardElements';
import {
  ThreeLines,
  TwoLines,
} from '../../../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import {
  isFirstStep as computeIsFirstStep,
  isLastStep as computeIsLastStep,
} from '../../../components/nav/StepsWithContent';
import { useResponsiveQueries } from '../../../components/responsive/responsiveQueries';
import { InlineLink } from '../../../components/typography';
import AddressLookup from '../../../dialogs/shipments/AddressLookup';
import { cssVariables } from '../../../styles/cssVariables';
import { pxToNumber } from '../../../utils/cssUtils';
import {
  AddressCountryFormItem,
  AddressFormItems,
} from '../../../widgets/addresses';
import { getPackageCount } from '../shipmentCommon';
import { mergeFormNames } from '../../../components/forms/formHelpers';
import { useNewShipmentElementsStyles } from './newShipmentElements.styles';

// eslint-disable-next-line
const SIMPLE_PHONE_REGEX = /^\s*[\d\s().+\-]+$/;

/**
 * This is to prevent TAB key to be able to go to inactive slide and break design
 */
function DisableFormItemsInInactiveSlide({ slideActive, children }) {
  return (
    <FormItemsOverrideContextProvider disabled={!slideActive}>
      {children}
    </FormItemsOverrideContextProvider>
  );
}

export function NewShipmentForm({
  name,
  className,
  form,
  formRef,
  onValuesChange,
  slideActive,
  children,
  initialValues,
  preserve,
}) {
  return (
    <DisableFormItemsInInactiveSlide slideActive={slideActive}>
      <NoLabelForm
        form={form}
        formRef={formRef}
        onValuesChange={onValuesChange}
        name={name}
        className={className}
        initialValues={initialValues}
        preserve={preserve}
      >
        {children}
      </NoLabelForm>
    </DisableFormItemsInInactiveSlide>
  );
}

export function NewShipmentBottomControls({
  submitStep,
  stepsProps,
  cancelComponent: Cancel = CancelShipmentLink,
}) {
  const { styles } = useNewShipmentElementsStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const packages = useDynamicOriginalValueDependency('packages');
  if (!isEmpty(packages)) {
    return null;
  }
  return (
    <div className="hide-lg-and-bigger spaces-vert-sm">
      <ThreeLines />
      <Row align="middle">
        <Col>
          <Cancel />
        </Col>
        <Col className={layoutStyles.flex1}>
          <div className={styles.newShipmentBottomControls}>
            <NewShipmentFormButtons
              onSubmit={submitStep}
              onBack={stepsProps.goToPrev}
              isFirstStep={computeIsFirstStep(stepsProps)}
              isLastStep={computeIsLastStep(stepsProps)}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export function CancelLink({ className, to = routes.monitor, messageId }) {
  const { styles } = useNewShipmentElementsStyles();
  return (
    <InlineLink className={classNames(styles.redLink, className)} to={to}>
      <FormattedMessage id={messageId} />
    </InlineLink>
  );
}

export function CancelShipmentLink({ className, to = routes.monitor }) {
  return (
    <CancelLink
      className={className}
      to={to}
      messageId="book.newShipment.cancelShipment"
    />
  );
}

export function NewShipmentFormButtons({
  onSubmit,
  onBack,
  isFirstStep,
  isLastStep,
  buttonProps,
  nextLabelId = 'buttons.saveNext',
  primaryButtonIcon,
}) {
  const isPackageEdited = !!useDynamicOriginalValueDependency('packages');
  const isPackageListEmpty =
    getPackageCount(
      useDynamicOriginalValueDependency('packageList.packages')
    ) === 0;
  const isPackageListTbd = useDynamicOriginalValueDependency('packageList.tbd');

  const submitDisabled =
    !isPackageListTbd && (isPackageEdited || isPackageListEmpty);

  const submitLabel = 'book.newShipment.review';

  return (
    <>
      <Button
        onClick={onBack}
        disabled={isFirstStep}
        data-subject="shipment"
        data-action="back"
        {...buttonProps}
      >
        <FAIcon icon="chevron-left" />
        <span>
          <FormattedMessage id="buttons.back" />
        </span>
      </Button>
      <AsyncCallbackButton
        type="primary"
        data-subject="shipment"
        data-action="save"
        callback={onSubmit}
        loadingTextId="buttons.validating"
        disabled={isLastStep && submitDisabled}
        {...buttonProps}
      >
        <span>
          <FormattedMessage id={isLastStep ? submitLabel : nextLabelId} />
        </span>
        {primaryButtonIcon || <FAIcon icon="chevron-right" />}
      </AsyncCallbackButton>
    </>
  );
}

export function NewShipmentSubmitButton({ form, submit, extractValues }) {
  const isPackageEdited = !!useDynamicOriginalValueDependency('packages');
  const isPackageListEmpty =
    getPackageCount(
      useDynamicOriginalValueDependency('packageList.packages')
    ) === 0;
  const isPackageListTbd = useDynamicOriginalValueDependency('packageList.tbd');

  const submitDisabled =
    !isPackageListTbd && (isPackageEdited || isPackageListEmpty);

  return (
    <AsyncCallbackButton
      type="primary"
      data-subject="shipment"
      data-action="save"
      disabled={submitDisabled}
      callback={async () => {
        form.formRef.current.customSubmit(async () => {
          await form.form.validateFields();
          submit(extractValues(), {});
        });
      }}
    >
      <FormattedMessage id="book.newShipment.review" />
    </AsyncCallbackButton>
  );
}

function NewShipmentResponsiveGutterRow({ children, className, ...rest }) {
  const media = useResponsiveQueries();
  const isSm = media.xs || media.sm;

  return (
    <Row
      gutter={pxToNumber(
        isSm ? cssVariables.spaceNorm : cssVariables.spaceNorm2
      )}
      className={className}
      {...rest}
    >
      {children}
    </Row>
  );
}

export const NewShipmentBaseRow = ({ className, gutter, ...rest }) => (
  <Row
    gutter={
      gutter || [
        pxToNumber(cssVariables.spaceNorm2),
        pxToNumber(cssVariables.spaceNorm),
      ]
    }
    {...rest}
    className={classNames('no-margin-form-items', className)}
  />
);

const BaseCol = props => (
  <Col {...props} xl={12} lg={12} md={12} sm={24} xs={24} />
);

const joinSchemaName = (schemaNamePrefix, suffix) =>
  schemaNamePrefix ? `${schemaNamePrefix}.${suffix}` : suffix;

export function JobLocationSection({
  title,
  titleId,
  rightContent,
  className,
  titleClassName,
  dividerClassName,
  children,
  bodyProps,
  requiredInTitle,
  ...rest
}) {
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles } = useNewShipmentElementsStyles(layoutStyles);
  return (
    <div
      className={classNames(
        'JobLocationCard-Section',
        styles.jobLocationCardSection,
        className
      )}
      {...rest}
    >
      {(title || titleId || rightContent) && (
        <Row
          className={classNames(
            'JobLocationCard-SectionHeader',
            styles.jobLocationCardSectionHeader,
            'flex-nowrap'
          )}
          align="middle"
          justify="space-between"
        >
          <Col
            className={classNames(
              'JobLocationCard-SectionTitle',
              styles.jobLocationCardSectionTitle,
              titleClassName
            )}
          >
            {titleId ? <FormattedMessage id={titleId} /> : title}
            {requiredInTitle && ' *'}
          </Col>
          <Col>{rightContent}</Col>
        </Row>
      )}
      <div
        {...bodyProps}
        className={classNames(
          styles.jobLocationCardSectionBody,
          bodyProps?.className
        )}
      >
        {children}
      </div>
      <TwoLines className={classNames('size-6', dividerClassName)} />
    </div>
  );
}

function JobLocationFirstCard({ titleId, schemaNamePrefix, children }) {
  const { styles } = useNewShipmentElementsStyles();
  const countrySection = (
    <JobLocationSection titleId="book.newShipment.label.countryTerritory">
      <AddressCountryFormItem
        parentName="address"
        parentSchemaName={joinSchemaName(schemaNamePrefix, 'address')}
      />
    </JobLocationSection>
  );
  return (
    <ConfigurableCard
      titleId={titleId}
      bodyComponent="div"
      bodyClassName={classNames(
        'JobLocationCardBody',
        styles.newShipmentCardBody,
        'no-margin-form-items'
      )}
    >
      <NewShipmentBaseRow align="top">
        {children ? (
          <>
            <BaseCol>{countrySection}</BaseCol>
            <BaseCol>{children}</BaseCol>
          </>
        ) : (
          <Col span={24}>{countrySection}</Col>
        )}
      </NewShipmentBaseRow>
    </ConfigurableCard>
  );
}

function AddressLookupSwitch({
  enable,
  headerExtra,
  countrySchemaName,
  children,
  namePrefix,
  countryCodeRestrict,
}) {
  const { styles } = useNewShipmentElementsStyles();
  if (!enable) {
    return children;
  }
  const {
    webResults: enableWebResults,
    addressBookResults: enableAddressBookResults,
  } = isObject(enable)
    ? enable
    : { webResults: true, addressBookResults: true };
  return (
    <AddressLookup
      namePrefix={namePrefix}
      countryCodeRestrict={countryCodeRestrict}
      renderLookup={field => (
        <JobLocationSection
          className="margin-top-sm"
          titleId="address.label.lookup"
          rightContent={headerExtra}
        >
          <div style={{ position: 'relative' }}>
            {field}
            <div style={{ display: 'none' }}>{children}</div>
          </div>
        </JobLocationSection>
      )}
      countrySchemaName={countrySchemaName}
      enableAddressBookResults={!!enableAddressBookResults}
      enableWebResults={!!enableWebResults}
    >
      {({ onReset }) => (
        <>
          {onReset && (
            <InlineLink
              className={classNames(
                styles.redLink,
                styles.newShipmentLinkAboveFirstSection
              )}
              onClick={onReset}
            >
              <FormattedMessage id="buttons.clearForm" />
            </InlineLink>
          )}
          <div>{children}</div>
        </>
      )}
    </AddressLookup>
  );
}

export function AddressLookupCard({
  titleId,
  schemaNamePrefix,
  renderHeaderExtra,
  enableAddressLookup,
  namePrefix,
  showCountry,
  countryCodeRestrict,
  showSpecialInstructions = true,
}) {
  const { styles } = useNewShipmentElementsStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { formatMessage: f } = useIntl();
  const respContext = useResponsiveQueries();
  const isSm = respContext.sm || respContext.xs;
  const isMd = isSm || respContext.md;
  return (
    <ConfigurableCard
      titleId={titleId}
      bodyComponent="div"
      bodyClassName={classNames(
        'JobLocationCardBody',
        styles.newShipmentCardBody,
        'no-margin-form-items'
      )}
    >
      <AddressLookupSwitch
        countryCodeRestrict={countryCodeRestrict}
        enable={enableAddressLookup}
        headerExtra={
          renderHeaderExtra && renderHeaderExtra({ type: 'addressLookup' })
        }
        countrySchemaName={joinSchemaName(schemaNamePrefix, 'address.country')}
        namePrefix={namePrefix}
      >
        <JobLocationSection
          className={isMd ? 'margin-top-norm2' : 'margin-top-sm'}
          titleId="address.label.address"
          rightContent={
            renderHeaderExtra && renderHeaderExtra({ type: 'addressSection' })
          }
        >
          {showCountry && (
            <AddressCountryFormItem
              parentName={mergeFormNames(namePrefix, 'address')}
              parentSchemaName={joinSchemaName(schemaNamePrefix, 'address')}
              disabled={!!countryCodeRestrict}
            />
          )}

          <AddressFormItems
            name={mergeFormNames(namePrefix, 'address')}
            schemaName={joinSchemaName(schemaNamePrefix, 'address')}
          />
          {showSpecialInstructions && (
            <FormItemInputText
              name={mergeFormNames(namePrefix, 'specialInstructions')}
              schemaName={joinSchemaName(
                schemaNamePrefix,
                'specialInstructions'
              )}
              labelId="book.newShipment.label.specialInstructions"
            />
          )}
        </JobLocationSection>
        <JobLocationSection titleId="contact.label.contact">
          <NewShipmentBaseRow gutter={isSm ? [] : undefined}>
            <BaseCol>
              <div>
                <FormItemInputText
                  name={mergeFormNames(namePrefix, 'companyName')}
                  schemaName={joinSchemaName(schemaNamePrefix, 'companyName')}
                  labelId="book.newShipment.label.company"
                />
              </div>
            </BaseCol>
            <BaseCol>
              <div>
                <FormItemInputText
                  name={mergeFormNames(namePrefix, ['contact', 'name'])}
                  schemaName={joinSchemaName(schemaNamePrefix, 'contact.name')}
                  labelId="book.newShipment.label.shipmentContact"
                />
              </div>
            </BaseCol>
          </NewShipmentBaseRow>
          <NewShipmentBaseRow gutter={isSm && []}>
            <BaseCol>
              <div>
                <FormItemInputText
                  name={mergeFormNames(namePrefix, ['contact', 'email'])}
                  schemaName={joinSchemaName(schemaNamePrefix, 'contact.email')}
                  labelId="book.newShipment.label.email"
                />
              </div>
            </BaseCol>
            <BaseCol>
              <NewShipmentResponsiveGutterRow>
                <Col className={layoutStyles.flex1}>
                  <FormItemInputText
                    name={mergeFormNames(namePrefix, ['contact', 'phone'])}
                    schemaName={joinSchemaName(
                      schemaNamePrefix,
                      'contact.phone'
                    )}
                    labelId="book.newShipment.label.phone"
                    rules={[
                      {
                        pattern: SIMPLE_PHONE_REGEX,
                        message: f({
                          id: 'book.newShipment.phone.invalidFormat',
                        }),
                      },
                    ]}
                  />
                </Col>
                <Col>
                  <FormItemInputText
                    className="width-60"
                    name={mergeFormNames(namePrefix, [
                      'contact',
                      'phoneExtension',
                    ])}
                    labelId="book.newShipment.label.phoneExtension"
                  />
                </Col>
              </NewShipmentResponsiveGutterRow>
            </BaseCol>
          </NewShipmentBaseRow>
        </JobLocationSection>
      </AddressLookupSwitch>
    </ConfigurableCard>
  );
}

export function JobLocationCards({
  firstTitleId,
  dateSection,
  secondTitleId,
  renderSecondHeaderExtra,
  schemaNamePrefix,
  enableAddressLookup,
}) {
  return (
    <div className="margins-bottom-norm2">
      <JobLocationFirstCard
        titleId={firstTitleId}
        schemaNamePrefix={schemaNamePrefix}
      >
        {dateSection}
      </JobLocationFirstCard>
      <AddressLookupCard
        titleId={secondTitleId}
        schemaNamePrefix={schemaNamePrefix}
        renderHeaderExtra={renderSecondHeaderExtra}
        enableAddressLookup={enableAddressLookup}
      />
    </div>
  );
}
